import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { enqueue } from '../lib/offline/offlineDb';
import { api } from '../lib/api';
import { useSettingsStore, type UserPreferences } from '../stores/useSettingsStore';
import type { AuthUser } from './useAuth';

type PreferencesPatch = Partial<UserPreferences>;

/**
 * Subscribe to preference changes in the settings store and sync them to the
 * backend. When online a direct PATCH fires; when offline (or when the PATCH
 * fails) the change is queued via the offline sync infrastructure and is
 * flushed the next time the network comes back (via `useOfflineSync`).
 *
 * The hook runs only after the store has been hydrated from /auth/me so the
 * initial hydration does not replay as a server write.
 */
export function useSyncUserPreferences(): void {
  const queryClient = useQueryClient();
  const lastSyncedRef = useRef<UserPreferences | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const unsubscribe = useSettingsStore.subscribe((state) => {
      if (!state.didHydrate) return;

      const current: UserPreferences = {
        multiCartEnabled: state.multiCartEnabled,
        favoriteGroupsEnabled: state.favoriteGroupsEnabled,
        showFavoritesOnHome: state.showFavoritesOnHome,
      };

      if (lastSyncedRef.current === null) {
        lastSyncedRef.current = current;
        return;
      }

      const patch: PreferencesPatch = {};
      (Object.keys(current) as (keyof UserPreferences)[]).forEach((k) => {
        if (current[k] !== lastSyncedRef.current![k]) {
          patch[k] = current[k];
        }
      });

      if (Object.keys(patch).length === 0) return;
      lastSyncedRef.current = current;

      // Keep the persisted react-query cache in sync so a reload shows the
      // latest preferences even before /auth/me refetches.
      queryClient.setQueryData<AuthUser | null>(['auth-user'], (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          preferences: {
            ...(prev.preferences ?? current),
            ...patch,
          },
        };
      });

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        void syncPatch(patch);
      }, 300);
    });

    return () => {
      unsubscribe();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [queryClient]);
}

async function syncPatch(patch: PreferencesPatch): Promise<void> {
  if (!navigator.onLine) {
    await enqueue('userPreferences', 'USER_PREFERENCES_UPDATE', patch as Record<string, unknown>);
    return;
  }

  try {
    await api('/user/preferences', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  } catch {
    // Network flaked mid-request — queue for later so we do not lose the change.
    await enqueue('userPreferences', 'USER_PREFERENCES_UPDATE', patch as Record<string, unknown>);
  }
}
